/**
 * Force-full Stripe payment surface for the Activities checkout (Step 3).
 *
 * Rendered only when Stripe is *fully* enabled for the account (`forceStripeFull`). Mirrors the RSI
 * `force-full-section` component so Activities offers exactly the same "ways of payment":
 *
 *   - A radio picker gated by the account's `payment_buttons` preference: Card, then the wallets
 *     (Apple Pay / Google Pay / Link / PayPal / Amazon Pay — additionally gated by Stripe's
 *     per-browser eligibility probe), then Klarna / Afterpay-Clearpay / Cash App Pay.
 *   - Card  -> an inline `<PaymentElement>` (in-element Apple/Google Pay buttons are suppressed because
 *             the ExpressCheckoutElement owns those) + a "Complete booking" button.
 *   - Klarna -> a wallet row in every respect: probe-gated, and picking it renders the same
 *             single-method `<ExpressCheckoutElement>` with Stripe's own Klarna button and popup.
 *   - Afterpay-Clearpay / Cash App Pay -> rows of their own next to the wallets, each rendering a branded
 *             button that behaves like a wallet button: click it and Stripe's own redirect (Afterpay) or
 *             QR modal (Cash App Pay) opens. Stripe's ExpressCheckoutElement has NO key for either, so
 *             there is no Stripe-rendered button to use — we draw it and call the method's dedicated
 *             confirm API, which needs no Elements (TRAV23-4560). Gated by currency, since Stripe
 *             validates `payment_method_types` against it.
 *   - Wallet -> a single-method `<ExpressCheckoutElement>` showing only the picked wallet's button.
 *
 * PaymentElement and the Complete button must share one `<Elements>` tree to call
 * `stripe.confirmPayment({ elements, ... })` correctly; the wallet flow uses its own tree because the
 * ExpressCheckoutElement is re-keyed on `paymentSelected` to enable only the selected wallet. We mount
 * one of two trees depending on `paymentSelected` (a PaymentElement method -> PaymentElement tree;
 * otherwise -> wallet tree). The picker is controlled by this component's state so the selection
 * survives the tree re-mount, and `multiplePayments` (the wallet probe result) is lifted here so it
 * survives swaps between the two trees.
 *
 * The PaymentElement tree pins `paymentMethodTypes` to the ONE method the selected row stands for, so
 * the Card row keeps collecting card only, exactly as before. The intent must declare the same method
 * or Stripe rejects the confirm, so the selection is passed out through `onCompleteBooking`. The wallet
 * tree needs none of that and stays on automatic payment methods, as it always has.
 *
 * Note on the ExpressCheckoutElement's `paymentMethods` option: every key Stripe supports has to be
 * listed. An omitted key defaults to 'auto', so it renders alongside the one the customer picked —
 * which is what made a stray Klarna button appear next to Amazon Pay before `klarna` was added.
 *
 * When the account has no `payment_buttons` preference there is nothing to pick between, so we skip the
 * picker and render only the card form + Complete button.
 */

import './style.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { Card, Radio, Divider, Spin, Button } from 'antd';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { isEmpty } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';

import { AmexCardSvg, MaestroCardSvg, MasterCardSvg, VisaCardSvg } from '@assets';
import { stripePromise, hasStripeExpressWalletMethods, getExpressCheckoutExtraMethods, getPaymentElementExtraMethods, isStripeDirectConfirmMethod } from '@share/utils';
import { AfterpayClearpaySvg, AmazonPaySvg, ApplePaySvg, CashAppPaySvg, GooglePaySvg, KlarnaPaySvg, LinkPaySvg, PaypalSvg } from '../../../../assets/icons/payment-select';

type CompleteBooking = (
  isCheckoutExpress: boolean,
  stripe: any,
  elements: any,
  paymentDescription?: string,
  /** Stripe `payment_method_types` token the PaymentElement is pinned to (`card`, `klarna`, …), so the
   *  BFF can create a PaymentIntent that declares the same method. Omitted on the wallet path, which
   *  uses automatic payment methods. */
  stripePaymentMethodType?: string,
) => Promise<boolean>;

interface IProps {
  /** Amount in minor units (cents) — must match the BFF PaymentIntent. */
  amount: number;
  currency: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  /** Account `payment_buttons` keys (e.g. ['google_pay','apple_pay',...]). Empty => card only. */
  enabledMethods?: string[];
  /** Rendered between the payment surface and the action buttons — e.g. the terms / cancellation
   *  acknowledgement checkboxes, so they sit right above "Complete booking" instead of scrolling
   *  off-screen above the picker. */
  footer?: React.ReactNode;
  onCompleteBooking: CompleteBooking;
  onBack: () => void;
}

/** Fallback until the PaymentElement reports a selection — it opens on card. */
const PAYMENT_ELEMENT_DEFAULT_METHOD = 'card';

/** BFF `PaymentDescripcion` for a payment taken through the PaymentElement. The element reports the
 *  method the customer actually chose via `onChange.value.type` (Stripe's `payment_method_types`
 *  spelling), so a Klarna or Cash App Pay booking is no longer labelled "Card" in backoffice.
 *  Mirrors front-rsi `force-full-section`. */
function paymentDescriptionFromElementType(elementPaymentType: string | undefined): string {
  const key = (elementPaymentType || PAYMENT_ELEMENT_DEFAULT_METHOD).toLowerCase();
  const map: Record<string, string> = {
    card: 'Card',
    afterpay_clearpay: 'Afterpay Clearpay',
    cashapp: 'Cash App Pay',
  };
  return map[key] ?? key.replace(/_/g, ' ');
}

/** Force-full PaymentElement: card form expanded, in-element wallet buttons suppressed (the
 *  ExpressCheckoutElement owns Apple/Google Pay). The `<Elements>` tree pins `paymentMethodTypes` to
 *  the single method the selected picker row stands for, so the element only ever renders that one
 *  method's form and this stays a plain display option. Mirrors front-rsi `force-full-section`. */
const FORCE_FULL_PAYMENT_ELEMENT_OPTIONS = {
  layout: {
    type: 'accordion' as const,
    defaultCollapsed: false,
  },
  wallets: {
    applePay: 'never' as const,
    googlePay: 'never' as const,
  },
};

/** Picker-row artwork for the methods added in TRAV23-4560. Stripe ships button artwork for the
 *  original wallets but not for these, so each row carries its own brand mark. */
const EXTRA_METHOD_ICONS: Record<string, React.FC> = {
  klarna: KlarnaPaySvg,
  afterpay_clearpay: AfterpayClearpaySvg,
  cashapp: CashAppPaySvg,
};

/** Picker-row labels for those methods — brand names, so not translated. */
const EXTRA_METHOD_LABELS: Record<string, string> = {
  klarna: 'Klarna',
  afterpay_clearpay: 'Afterpay / Clearpay',
  cashapp: 'Cash App Pay',
};

/** Maps the Stripe ExpressCheckoutElement `expressPaymentType` to the persisted wallet description. */
function paymentDescriptionFromExpressType(expressPaymentType?: string): string {
  if (!expressPaymentType) return 'Express checkout';
  const key = String(expressPaymentType).toLowerCase().replace(/-/g, '_');
  const map: Record<string, string> = {
    google_pay: 'Google pay',
    apple_pay: 'Apple pay',
    amazon_pay: 'Amazon pay',
    paypal: 'Paypal',
    link: 'Link',
    klarna: 'Klarna',
  };
  return map[key] ?? String(expressPaymentType).replace(/_/g, ' ');
}

export const ActivityForceFullStripeCheckout: React.FC<IProps> = ({
  amount,
  currency,
  isDisabled,
  isLoading,
  enabledMethods = [],
  footer,
  onCompleteBooking,
  onBack,
}) => {
  const [paymentSelected, setPaymentSelected] = useState<string>('');
  const [multiplePayments, setMultiplePayments] = useState<any>({});
  /** True until Stripe's ExpressCheckoutElement first reports `availablePaymentMethods`. While true the
   *  picker shows a spinner instead of partial rows. Flipped off once and stays off for the session. */
  const [isProbing, setIsProbing] = useState<boolean>(true);
  /** Whether the card PaymentElement has all required fields filled (from its `onChange.complete`).
   *  Greys out "Complete booking" until the card is complete. */
  const [cardComplete, setCardComplete] = useState<boolean>(false);

  /** The probe resolves on the ExpressCheckoutElement's first `onReady`, which never fires when the
   *  browser has no eligible wallet at all — without a cap the picker would spin forever. Mirrors the
   *  8s fallback in front-rsi. Newly relevant now that an account can enable only Klarna / Afterpay /
   *  Cash App Pay and no wallet. */
  useEffect(() => {
    const timer = window.setTimeout(() => setIsProbing(false), 8000);
    return () => window.clearTimeout(timer);
  }, []);

  const paymentButtonKeys = enabledMethods;
  /** Afterpay / Cash App Pay this account has enabled and that Stripe accepts for this currency.
   *  Each becomes a picker row with a branded button, confirmed through its own Stripe API — the
   *  ExpressCheckoutElement has no key for either. */
  const directConfirmMethods = useMemo(
    () => getPaymentElementExtraMethods(paymentButtonKeys, currency),
    [paymentButtonKeys, currency],
  );
  /** Klarna — a wallet row like Google/Apple/Link, still subject to Stripe's eligibility probe. */
  const expressCheckoutExtraMethods = useMemo(
    () => getExpressCheckoutExtraMethods(paymentButtonKeys),
    [paymentButtonKeys],
  );

  /** Card is the only row still backed by a `<PaymentElement>`. */
  const isCardRow = paymentSelected === PAYMENT_ELEMENT_DEFAULT_METHOD;
  /** Afterpay / Cash App Pay: branded button + dedicated confirm API, no Elements involved. */
  const isDirectConfirmRow =
    isStripeDirectConfirmMethod(paymentSelected) && directConfirmMethods.includes(paymentSelected);

  // Reset completeness whenever the selection changes — the `<Elements>` tree re-mounts with an empty
  // PaymentElement, so a stale `true` from a previous visit must not enable the button.
  useEffect(() => {
    setCardComplete(false);
  }, [paymentSelected]);

  // Wallet tree — leave method selection to the ExpressCheckoutElement.
  const walletOptions = useMemo(
    () => ({
      mode: 'payment' as const,
      // Must match the BFF PaymentIntent (manual capture). Capture happens after the provider booking
      // succeeds; on failure the authorization is voided (no refund).
      captureMethod: 'manual' as const,
      amount,
      currency: (currency ?? 'usd').toLowerCase(),
    }),
    [amount, currency],
  );

  /** PaymentElement tree — card only, exactly as before; the other rows never mount it. */
  const paymentElementOptions = useMemo(
    () => ({
      ...walletOptions,
      paymentMethodTypes: [PAYMENT_ELEMENT_DEFAULT_METHOD],
    }),
    [walletOptions],
  );

  const noWalletPrefs = isEmpty(enabledMethods);

  // No preferences at all -> nothing to pick between; render the card form + Complete button only.
  if (noWalletPrefs) {
    return (
      <Elements stripe={stripePromise} options={paymentElementOptions} key="ffsc-card-only">
        <div className="activity-force-full">
          <Card className="activity-force-full__picker mb-3">
            <PaymentElement
              options={FORCE_FULL_PAYMENT_ELEMENT_OPTIONS}
              onChange={(e) => setCardComplete(!!e.complete)}
            />
          </Card>
          {footer}
          <CardCompleteAction
            isDisabled={isDisabled}
            isLoading={isLoading}
            cardComplete={cardComplete}
            paymentElementMethod={PAYMENT_ELEMENT_DEFAULT_METHOD}
            onCompleteBooking={onCompleteBooking}
            onBack={onBack}
          />
        </div>
      </Elements>
    );
  }

  if (isCardRow) {
    return (
      <Elements stripe={stripePromise} options={paymentElementOptions} key="ffsc-card">
        <div className="activity-force-full">
          <PickerBox
            paymentSelected={paymentSelected}
            onSelectMethod={setPaymentSelected}
            multiplePayments={multiplePayments}
            isProbing={isProbing}
            enabledMethods={enabledMethods}
            directConfirmMethods={directConfirmMethods}
            expressCheckoutExtraMethods={expressCheckoutExtraMethods}
            inlineElement={
              <div className="activity-force-full__card-inline">
                <PaymentElement
                  options={FORCE_FULL_PAYMENT_ELEMENT_OPTIONS}
                  onChange={(e) => setCardComplete(!!e.complete)}
                />
              </div>
            }
          />
          {footer}
          <CardCompleteAction
            isDisabled={isDisabled}
            isLoading={isLoading}
            cardComplete={cardComplete}
            paymentElementMethod={PAYMENT_ELEMENT_DEFAULT_METHOD}
            onCompleteBooking={onCompleteBooking}
            onBack={onBack}
          />
        </div>
      </Elements>
    );
  }

  if (isDirectConfirmRow) {
    // No Elements tree of its own: the dedicated confirm APIs take a clientSecret, not an Element.
    // We still mount one so `useStripe()` resolves inside `CardCompleteAction`.
    return (
      <Elements stripe={stripePromise} options={walletOptions} key="ffsc-direct">
        <div className="activity-force-full">
          <PickerBox
            paymentSelected={paymentSelected}
            onSelectMethod={setPaymentSelected}
            multiplePayments={multiplePayments}
            isProbing={isProbing}
            enabledMethods={enabledMethods}
            directConfirmMethods={directConfirmMethods}
            expressCheckoutExtraMethods={expressCheckoutExtraMethods}
            inlineElement={null}
          />
          {footer}
          <CardCompleteAction
            isDisabled={isDisabled}
            isLoading={isLoading}
            // Nothing to fill in here: Afterpay collects on its own site, Cash App Pay in its QR modal.
            cardComplete
            paymentElementMethod={paymentSelected}
            onCompleteBooking={onCompleteBooking}
            onBack={onBack}
          />
        </div>
      </Elements>
    );
  }

  return (
    <Elements stripe={stripePromise} options={walletOptions} key="ffsc-wallet">
      <div className="activity-force-full">
        <PickerBox
          paymentSelected={paymentSelected}
          onSelectMethod={setPaymentSelected}
          multiplePayments={multiplePayments}
          isProbing={isProbing}
          enabledMethods={enabledMethods}
          directConfirmMethods={directConfirmMethods}
          expressCheckoutExtraMethods={expressCheckoutExtraMethods}
          inlineElement={null}
        />
        {footer}
        <WalletECAction
          paymentSelected={paymentSelected}
          enabledMethods={enabledMethods}
          onMultiplePaymentsResolved={setMultiplePayments}
          onProbeComplete={() => setIsProbing(false)}
          isDisabled={isDisabled}
          isLoading={isLoading}
          onCompleteBooking={onCompleteBooking}
          onBack={onBack}
        />
      </div>
    </Elements>
  );
};

/* ────────────────────────────────────────────────────────────────────────── Picker box ───────── */

interface PickerBoxProps {
  paymentSelected: string;
  onSelectMethod: (value: string) => void;
  multiplePayments: any;
  /** While true the WALLET rows are withheld — Stripe is still probing which wallets are eligible.
   *  Card and the PaymentElement-only methods do not depend on that probe and render immediately. */
  isProbing: boolean;
  enabledMethods: string[];
  /** Afterpay / Cash App Pay rows to render (already filtered by preference + currency). Backed by a
   *  branded button + the method's dedicated confirm API. */
  directConfirmMethods: string[];
  /** Klarna row(s) to render (already filtered by preference). Backed by the ExpressCheckoutElement,
   *  so additionally gated below by the eligibility probe, exactly like the wallets. */
  expressCheckoutExtraMethods: string[];
  /** Rendered immediately under the selected PaymentElement row (Card or one of the extras). */
  inlineElement: React.ReactNode;
}

function PickerBox({
  paymentSelected,
  onSelectMethod,
  multiplePayments,
  isProbing,
  enabledMethods,
  directConfirmMethods,
  expressCheckoutExtraMethods,
  inlineElement,
}: PickerBoxProps) {
  const intl = useIntl();
  const noWalletPrefs = isEmpty(enabledMethods);

  /** Card first, then the wallets (Klarna among them), then the PaymentElement-only methods — so all
   *  three new methods sit alongside Google Pay and friends as peer choices.
   *
   *  The whole set is withheld behind a spinner until the wallet probe resolves: the probe lives in
   *  `WalletECAction`, which is only mounted on the wallet branch, so letting the customer pick a
   *  PaymentElement row first would unmount it and the wallet rows would never appear. The parent
   *  caps the wait (see its probe fallback), so the spinner cannot outlive the probe. */
  const options = [
    {
      value: 'card',
      label: <FormattedMessage id="debit.credit.card" />,
      Icon: () => (
        <div className="activity-force-full__card-icons">
          <MaestroCardSvg />
          <MasterCardSvg />
          <VisaCardSvg />
          <AmexCardSvg />
        </div>
      ),
    },
    !noWalletPrefs &&
      multiplePayments?.googlePay &&
      enabledMethods.includes('google_pay') && {
        value: 'google_pay',
        label: 'Google Pay',
        Icon: GooglePaySvg,
      },
    !noWalletPrefs &&
      multiplePayments?.applePay &&
      enabledMethods.includes('apple_pay') && {
        value: 'apple_pay',
        label: 'Apple Pay',
        Icon: ApplePaySvg,
      },
    !noWalletPrefs &&
      multiplePayments?.link &&
      enabledMethods.includes('link') && {
        value: 'link',
        label: 'Link',
        Icon: LinkPaySvg,
      },
    !noWalletPrefs &&
      multiplePayments?.paypal &&
      enabledMethods.includes('paypal') && {
        value: 'paypal',
        label: 'PayPal',
        Icon: PaypalSvg,
      },
    !noWalletPrefs &&
      multiplePayments?.amazonPay &&
      enabledMethods.includes('amazon_pay') && {
        value: 'amazon_pay',
        label: 'Amazon Pay',
        Icon: AmazonPaySvg,
      },
    // Klarna & co: wallet rows, so gated by the same eligibility probe as Google/Apple/Link.
    ...expressCheckoutExtraMethods.map((method) =>
      !noWalletPrefs && multiplePayments?.[method]
        ? {
            value: method,
            label: EXTRA_METHOD_LABELS[method] ?? method,
            Icon: EXTRA_METHOD_ICONS[method],
          }
        : false,
    ),
    // Afterpay / Cash App Pay: no express button exists, so no probe to gate on.
    ...directConfirmMethods.map((method) => ({
      value: method,
      label: EXTRA_METHOD_LABELS[method] ?? method,
      Icon: EXTRA_METHOD_ICONS[method],
    })),
  ].filter(Boolean) as { value: string; label: React.ReactNode; Icon: React.FC }[];

  return (
    <Card className="activity-force-full__picker mb-3" id="payment-methods-select__card">
      <p className="activity-force-full__title">{intl.formatMessage({ id: 'select.payment.to.pay' })}</p>
      {isProbing ? (
        <div className="activity-force-full__probe-loader">
          <Spin />
        </div>
      ) : (
      <Radio.Group
        onChange={(e) => onSelectMethod(e.target.value)}
        value={paymentSelected}
        className="activity-force-full__radio-group"
      >
        {options.map((opt, index) => {
          const isLast = index === options.length - 1;
          const showInline = paymentSelected === opt.value && inlineElement;
          return (
            <div key={opt.value} className="activity-force-full__option-wrapper">
              <div className="activity-force-full__option">
                <Radio value={opt.value} className="activity-force-full__radio">
                  <span className="activity-force-full__option-text">{opt.label}</span>
                </Radio>
                <opt.Icon />
              </div>
              {showInline && (
                <div className="activity-force-full__inline-slot">{inlineElement}</div>
              )}
              {!isLast && <Divider className="activity-force-full__divider" />}
            </div>
          );
        })}
      </Radio.Group>
      )}
    </Card>
  );
}

/* ───────────────────────────────────────────────────────── Card path — Complete button ────────── */

interface CardCompleteActionProps {
  isDisabled?: boolean;
  isLoading?: boolean;
  /** PaymentElement completeness — button stays greyed out until the required fields are filled. */
  cardComplete?: boolean;
  /** Method selected inside the PaymentElement (`card`, `klarna`, `cashapp`, …). */
  paymentElementMethod: string;
  onCompleteBooking: CompleteBooking;
  onBack: () => void;
}

function CardCompleteAction({ isDisabled, isLoading, cardComplete, paymentElementMethod, onCompleteBooking, onBack }: CardCompleteActionProps) {
  const stripe = useStripe();
  const elements = useElements();

  /** Afterpay / Cash App Pay: no Elements to collect or submit, just a branded button that hands the
   *  clientSecret to the method's own confirm API. */
  const isDirectConfirm = isStripeDirectConfirmMethod(paymentElementMethod);

  const buttonDisabled = Boolean(
    isDisabled || !stripe || (!isDirectConfirm && !elements) || isLoading || !cardComplete,
  );

  const runSubmit = async () => {
    if (!stripe) return;

    // Only the card path collects through Elements, so only it has anything to submit.
    if (!isDirectConfirm) {
      if (!elements) return;
      const { error: submitError } = await elements.submit();
      if (submitError) return;
    }
    await onCompleteBooking(
      true,
      stripe,
      elements,
      paymentDescriptionFromElementType(paymentElementMethod),
      paymentElementMethod,
    );
  };

  return (
    <div className="activity-force-full__action activity-force-full__action--card">
      {isDirectConfirm ? (
        // Stands in for the Stripe-rendered wallet button: same slot, same one-click behaviour, and
        // clicking it opens Afterpay's redirect or Cash App Pay's QR modal.
        <button
          type="button"
          className={`activity-force-full__direct-button activity-force-full__direct-button--${paymentElementMethod}`}
          onClick={runSubmit}
          disabled={buttonDisabled}
        >
          {EXTRA_METHOD_LABELS[paymentElementMethod] ?? paymentElementMethod}
        </button>
      ) : (
        <Button className="next-btn" onClick={runSubmit} disabled={buttonDisabled} loading={isLoading}>
          <FormattedMessage id="activity.checkout.complete_booking.button" defaultMessage="Complete Booking" />
        </Button>
      )}
      <Button type="link" className="back-button" onClick={onBack} disabled={isLoading}>
        <FormattedMessage id="checkout.back" defaultMessage="Back" />
      </Button>
    </div>
  );
}

/* ─────────────────────────────── Wallet path — single-method ExpressCheckoutElement / probe ────── */

interface WalletECActionProps {
  paymentSelected: string;
  enabledMethods: string[];
  onMultiplePaymentsResolved: (available: any) => void;
  /** Called after Stripe's first `onReady` so the parent can flip its `isProbing` flag off. */
  onProbeComplete: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  onCompleteBooking: CompleteBooking;
  onBack: () => void;
}

function WalletECAction({
  paymentSelected,
  enabledMethods,
  onMultiplePaymentsResolved,
  onProbeComplete,
  isDisabled,
  isLoading,
  onCompleteBooking,
  onBack,
}: WalletECActionProps) {
  const stripe = useStripe();
  const elements = useElements();

  /** Probe-mode (`paymentSelected === ''`): enable every method in prefs so onReady can report
   *  eligibility. Selected mode: enable only the chosen one, force the rest to 'never'.
   *
   *  EVERY key Stripe supports has to be listed, including the ones we don't want. An omitted key
   *  defaults to 'auto', so leaving `klarna` out made Stripe render a Klarna button next to the
   *  selected wallet whenever the Stripe account had Klarna on. */
  const paymentMethods = useMemo(
    () => ({
      amazonPay: enabledMethods.includes('amazon_pay')
        ? paymentSelected === 'amazon_pay' || paymentSelected === ''
          ? ('auto' as const)
          : ('never' as const)
        : ('never' as const),
      applePay: enabledMethods.includes('apple_pay')
        ? paymentSelected === 'apple_pay' || paymentSelected === ''
          ? ('always' as const)
          : ('never' as const)
        : ('never' as const),
      googlePay: enabledMethods.includes('google_pay')
        ? paymentSelected === 'google_pay' || paymentSelected === ''
          ? ('always' as const)
          : ('never' as const)
        : ('never' as const),
      link: enabledMethods.includes('link')
        ? paymentSelected === 'link' || paymentSelected === ''
          ? ('auto' as const)
          : ('never' as const)
        : ('never' as const),
      paypal: enabledMethods.includes('paypal')
        ? paymentSelected === 'paypal' || paymentSelected === ''
          ? ('auto' as const)
          : ('never' as const)
        : ('never' as const),
      klarna: enabledMethods.includes('klarna')
        ? paymentSelected === 'klarna' || paymentSelected === ''
          ? ('auto' as const)
          : ('never' as const)
        : ('never' as const),
    }),
    [paymentSelected, enabledMethods],
  );

  const onConfirm = async (event: any) => {
    if (!stripe || !elements) return;
    const { error: submitError } = await elements.submit();
    if (submitError) return;
    const paymentDescription = paymentDescriptionFromExpressType(event?.expressPaymentType);
    try {
      const success = await onCompleteBooking(true, stripe, elements, paymentDescription);
      if (!success) {
        event.paymentFailed?.({ reason: 'fail' });
      }
    } catch {
      event.paymentFailed?.({ reason: 'fail' });
    }
  };

  const onReady = (event: any) => {
    const available = event?.availablePaymentMethods;
    if (available === undefined || available === null) return;
    // Flip the probe flag off on any onReady so the spinner is replaced once we have data.
    onProbeComplete();
    // Only refresh `multiplePayments` during the initial all-wallets probe. After the user picks a
    // wallet the EC is re-keyed with a single-method config, and Stripe's next onReady reports only
    // that method — using it would wipe the other rows and trap the user on their first pick.
    if (paymentSelected) return;
    onMultiplePaymentsResolved(hasStripeExpressWalletMethods(available) ? available : {});
  };

  // EC is hidden until the user picks a wallet — Back must still render so the user can exit.
  const showEC = !!paymentSelected;

  return (
    <div className="activity-force-full__action activity-force-full__action--wallet">
      <div
        className="activity-force-full__wallet-element"
        style={{
          display: showEC ? 'block' : 'none',
          position: 'relative',
          // Block the wallet button until the acknowledgements are accepted (isDisabled).
          opacity: isDisabled ? 0.5 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        <ExpressCheckoutElement
          key={JSON.stringify(paymentMethods)}
          onConfirm={onConfirm}
          onReady={onReady}
          options={{
            paymentMethods,
            paymentMethodOrder: ['applePay', 'googlePay', 'link', 'paypal', 'amazonPay', 'klarna'],
          }}
        />
        {isDisabled && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'transparent',
              cursor: 'not-allowed',
              zIndex: 10,
            }}
          />
        )}
      </div>
      <Button type="link" className="back-button" onClick={onBack} disabled={isLoading}>
        <FormattedMessage id="checkout.back" defaultMessage="Back" />
      </Button>
    </div>
  );
}
